import { Subscription } from 'rxjs';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgIf } from '@angular/common';
import { EventService } from '../../../core/services/event.service';
import RecordRTC from 'recordrtc';

@Component({
    selector: 'camera',
    templateUrl: './camera.component.html',
    styleUrls: ['./camera.component.scss'],
    standalone: true,
    imports: [NgIf]
})
export class CameraComponent implements OnInit, OnDestroy, AfterViewInit {
	private subs = new Subscription();

	public cameraError: string = '';
	private localStream: any;
	private recorder: any;
	private cameraRequestId: number = 0;

	@ViewChild('video') videoElem: any;

	constructor(private eventService: EventService) {}

	ngOnInit() {
		this.subs.add(this.eventService.subscribe('dismiss-camera', () => {
			this.stopTracks();
		}));

		this.subs.add(this.eventService.subscribe('start-recording-camera', () => {
			if (this.localStream?.active) {
				this.recorder = new RecordRTC(this.localStream, { type: 'video' });
				const video: HTMLVideoElement = this.videoElem.nativeElement;
				video.srcObject = this.localStream;
				this.playWhenReady(video);
				this.recorder.startRecording();
			}
		}));

		this.subs.add(this.eventService.subscribe('stop-recording-camera', () => {
			if (this.localStream?.active) {
				this.videoElem.nativeElement.muted = true;
				this.videoElem.nativeElement.volume = 0;
				this.recorder.stopRecording(this.stopRecordingCallback.bind(this));
			}
		}));

		this.subs.add(this.eventService.subscribe('camera-on', () => {
			this.recorder = new RecordRTC(this.localStream, { type: 'video' });
			const video: HTMLVideoElement = this.videoElem.nativeElement;
			video.muted = true;
			video.volume = 0;
			video.srcObject = this.localStream;
			this.playWhenReady(video);
		}));
	}

	ngOnDestroy() {
		this.subs.unsubscribe();
		this.stopTracks();
	}

	ngAfterViewInit() {
		const video: HTMLVideoElement = this.videoElem.nativeElement;
		video.autoplay = true;
		video.muted = true;
		video.volume = 0;
		video.playsInline = true;
		this.captureCamera();
	}

	captureCamera() {
		this.cameraError = '';
		const requestId = ++this.cameraRequestId;
		navigator.mediaDevices
			.getUserMedia({ audio: true, video: true })
			.then((camera) => {
				if (requestId !== this.cameraRequestId) {
					camera.getTracks().forEach(t => t.stop());
					return;
				}
				this.successCallback(camera);
			})
			.catch((error) => {
				if (requestId !== this.cameraRequestId) return;
				this.errorCallback(error);
			});
	}

	successCallback(camera: MediaStream) {
		this.localStream = camera;
		this.eventService.broadcast('camera-started', {});

		const video: HTMLVideoElement = this.videoElem.nativeElement;
		video.srcObject = camera;
		video.muted = true;
		this.playWhenReady(video);
	}

	errorCallback(error: any) {
		this.eventService.broadcast('camera-error', { error });
		if (error?.name === 'NotAllowedError' || error?.name === 'PermissionDeniedError') {
			this.cameraError = 'Camera access was denied. Please allow camera access in your browser settings.';
		} else if (error?.name === 'NotFoundError' || error?.name === 'DevicesNotFoundError') {
			this.cameraError = 'No camera was found on this device.';
		} else {
			this.cameraError = 'Camera could not be started. Please check your camera and try again.';
		}
	}

	stopRecordingCallback(videoURL: any) {
		const video: HTMLVideoElement = this.videoElem.nativeElement;
		video.srcObject = null;
		const recordedBlob = this.recorder.getBlob();
		video.src = URL.createObjectURL(recordedBlob);
		video.muted = false;
		video.volume = 1;
		this.playWhenReady(video);

		if (recordedBlob !== undefined) {
			this.eventService.broadcast('camera-recording-complete', {
				videoData: recordedBlob,
				url: videoURL,
			});
		}
	}

	private playWhenReady(video: HTMLVideoElement) {
		const tryPlay = () => video.play().catch(() => { video.controls = true; });
		if (video.readyState >= 1) {
			tryPlay();
		} else {
			video.addEventListener('loadedmetadata', tryPlay, { once: true });
		}
	}

	private stopTracks() {
		this.localStream?.getTracks().forEach((track: MediaStreamTrack) => track.stop());
	}
}
